// WeeklyReport.js - Full Weekly SEO & SEM Performance Report
// Fixes: Domain switching, adds Position Tracking, Social Media, SEM/Google Ads,
// GA4 Traffic Channels, Pageviews, Device, Audience Insights, Conversions
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDomain } from '../contexts/DomainContext';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/authService';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import {
  Download, RefreshCw, Calendar, TrendingUp, TrendingDown, Globe, Search,
  MousePointerClick, Eye, BarChart3, Target, ArrowUpRight, ArrowDownRight,
  Minus, FileText, Loader2, ChevronDown, AlertCircle, Users, Monitor,
  Smartphone, Tablet, Share2, DollarSign, Activity, Layout,
  MapPin, Link2, ExternalLink, PieChart
} from 'lucide-react';

const COLORS = ['#3b82f6','#8b5cf6','#10b981','#f59e0b','#ef4444','#ec4899',
  '#06b6d4','#84cc16','#f97316','#6366f1'];

/* ── tiny donut SVG ──────────────────────────── */
const MiniDonut = ({ data, size = 160 }) => {
  const total = data.reduce((s, d) => s + d.value, 0);
  if (!total) return null;
  const cx = size / 2, cy = size / 2, r = size * 0.35, r2 = size * 0.22;
  let cumulative = 0;
  const slices = data.map((d, i) => {
    const pct = d.value / total;
    const startAngle = cumulative * 2 * Math.PI - Math.PI / 2;
    cumulative += pct;
    const endAngle = cumulative * 2 * Math.PI - Math.PI / 2;
    const largeArc = pct > 0.5 ? 1 : 0;
    const x1 = cx + r * Math.cos(startAngle), y1 = cy + r * Math.sin(startAngle);
    const x2 = cx + r * Math.cos(endAngle), y2 = cy + r * Math.sin(endAngle);
    const ix1 = cx + r2 * Math.cos(endAngle), iy1 = cy + r2 * Math.sin(endAngle);
    const ix2 = cx + r2 * Math.cos(startAngle), iy2 = cy + r2 * Math.sin(startAngle);
    return (<path key={i} d={`M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} L ${ix1} ${iy1} A ${r2} ${r2} 0 ${largeArc} 0 ${ix2} ${iy2} Z`}
      fill={COLORS[i % COLORS.length]} />);
  });
  return <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>{slices}</svg>;
};

/* ── mini bar chart SVG ────────────────────────── */
const MiniBar = ({ data, height = 120, width = 400 }) => {
  if (!data || !data.length) return null;
  const maxVal = Math.max(...data.map(d => d.value), 1);
  const barW = Math.max(8, Math.min(30, (width - 20) / data.length - 4));
  return (
    <svg width={width} height={height + 20} viewBox={`0 0 ${width} ${height + 20}`}>
      {data.map((d, i) => {
        const bh = (d.value / maxVal) * height * 0.85;
        const x = 10 + i * (barW + 4);
        return (<g key={i}>
          <rect x={x} y={height - bh} width={barW} height={bh} rx={2}
            fill={COLORS[i % COLORS.length]} opacity={0.85} />
          <text x={x + barW / 2} y={height + 14} textAnchor="middle"
            fontSize={8} fill="#94a3b8">{(d.label || '').substring(0, 6)}</text>
        </g>);
      })}
    </svg>
  );
};


const WeeklyReport = () => {
  const { selectedDomain, domains, selectDomain } = useDomain();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [dateRange, setDateRange] = useState('7d');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [rateLimited, setRateLimited] = useState(false);
  const reportRef = useRef(null);
  const prevDomainRef = useRef(null);

  // Always use selectedDomain from context - this is what the sidebar controls
  const currentDomain = selectedDomain || (domains && domains.length > 0 ? domains[0].domain : '');

  const isRateLimitError = (result) => {
    if (result.status === 'rejected') {
      return result.reason?.response?.status === 429;
    }
    return false;
  };

  // Helper to compute date ranges
  const getDateRanges = useCallback(() => {
    const now = new Date();
    const endDate = new Date(now.getTime() - 86400000).toISOString().split('T')[0];
    let days = 7;
    if (dateRange === '28d') days = 28;
    else if (dateRange === '3m') days = 90;
    // Custom range: use user-picked dates when both are set
    if (dateRange === 'custom' && customStart && customEnd) {
      const cs = new Date(customStart);
      const ce = new Date(customEnd);
      const span = Math.max(1, Math.round((ce.getTime() - cs.getTime()) / 86400000));
      const cStart = customStart;
      const cEnd = customEnd;
      const cPrevEnd = new Date(cs.getTime() - 86400000).toISOString().split('T')[0];
      const cPrevStart = new Date(cs.getTime() - (span + 1) * 86400000).toISOString().split('T')[0];
      return { startDate: cStart, endDate: cEnd, prevStartDate: cPrevStart, prevEndDate: cPrevEnd };
    }
    const startDate = new Date(now.getTime() - days * 86400000).toISOString().split('T')[0];
    const prevEndDate = new Date(new Date(startDate).getTime() - 86400000).toISOString().split('T')[0];
    const prevStartDate = new Date(new Date(prevEndDate).getTime() - days * 86400000).toISOString().split('T')[0];
    return { startDate, endDate, prevStartDate, prevEndDate };
  }, [dateRange, customStart, customEnd]);

  const fetchReportData = useCallback(async () => {
    if (!currentDomain) return;
    setLoading(true);
    setError(null);
    setRateLimited(false);
    prevDomainRef.current = currentDomain;

    try {
      // Step 1: Get connected Google account & resolve GSC siteUrl + GA4 propertyId
      let gscAccountId = null;
      let resolvedSiteUrl = null;
      let ga4PropertyId = null;

      try {
        const acctRes = await api.get('/api/integrations/accounts');
        const accounts = acctRes.data?.accounts || [];
        const ga = accounts.find(a => a.provider === 'google' && a.status === 'active');
        if (ga) {
          gscAccountId = ga.id;

          // Try to get saved settings for this domain
          try {
            const settingsRes = await api.get(`/api/integrations/accounts/${ga.id}/settings/${encodeURIComponent(currentDomain)}`);
            const settings = settingsRes.data?.settings;
            if (settings?.gscSiteUrl) resolvedSiteUrl = settings.gscSiteUrl;
            if (settings?.ga4PropertyId) ga4PropertyId = settings.ga4PropertyId;
          } catch (e) { /* no saved settings */ }

          // If no saved settings, try matching from properties list
          if (!resolvedSiteUrl || !ga4PropertyId) {
            const propRes = await api.get(`/api/integrations/google/properties/${ga.id}`);
            const sites = propRes.data?.searchConsoleSites || [];
            const props = propRes.data?.properties || [];
            const domainClean = currentDomain.replace(/^https?:\/\//, '').replace(/\/$/, '').toLowerCase();

            if (!resolvedSiteUrl) {
              const matched = sites.find(s => {
                const sc = s.siteUrl.replace(/^https?:\/\//, '').replace(/^sc-domain:/, '').replace(/\/$/, '').toLowerCase();
                return sc === domainClean || sc === 'www.' + domainClean || domainClean === 'www.' + sc || sc.includes(domainClean) || domainClean.includes(sc);
              });
              if (matched) resolvedSiteUrl = matched.siteUrl;
            }
            if (!ga4PropertyId && props.length > 0) {
              // Try matching by domain name in display name
              const matched = props.find(p => p.displayName && p.displayName.toLowerCase().includes(domainClean.split('.')[0]));
              ga4PropertyId = matched ? matched.propertyId : props[0].propertyId;
            }
          }
        }
      } catch (e) {
        console.warn('Could not resolve Google accounts:', e);
      }

      const { startDate, endDate } = getDateRanges();

      // Step 2: Fetch all data sources in parallel
      const fetchPromises = [];

      // GSC dashboard summary
      fetchPromises.push(
        resolvedSiteUrl && gscAccountId
          ? api.post(`/api/integrations/google/dashboard-summary/${gscAccountId}`, {
              siteUrl: resolvedSiteUrl, propertyId: ga4PropertyId, startDate, endDate
            })
          : Promise.reject(new Error('No GSC for ' + currentDomain))
      );

      // GA4: Traffic channels (sessionDefaultChannelGroup)
      fetchPromises.push(
        ga4PropertyId && gscAccountId
          ? api.post(`/api/integrations/google/ga4/report/${gscAccountId}`, {
              propertyId: ga4PropertyId, startDate, endDate,
              dimensions: ['sessionDefaultChannelGroup'],
              metrics: ['sessions', 'totalUsers', 'screenPageViews', 'bounceRate'],
              limit: 20,
              orderBys: [{ metric: { metricName: 'sessions' }, desc: true }]
            })
          : Promise.reject(new Error('No GA4'))
      );

      // GA4: Top pages (pagePath)
      fetchPromises.push(
        ga4PropertyId && gscAccountId
          ? api.post(`/api/integrations/google/ga4/report/${gscAccountId}`, {
              propertyId: ga4PropertyId, startDate, endDate,
              dimensions: ['pagePath'],
              metrics: ['screenPageViews', 'sessions', 'averageSessionDuration', 'engagementRate'],
              limit: 15,
              orderBys: [{ metric: { metricName: 'screenPageViews' }, desc: true }]
            })
          : Promise.reject(new Error('No GA4'))
      );

      // GA4: Session source/medium
      fetchPromises.push(
        ga4PropertyId && gscAccountId
          ? api.post(`/api/integrations/google/ga4/report/${gscAccountId}`, {
              propertyId: ga4PropertyId, startDate, endDate,
              dimensions: ['sessionSourceMedium'],
              metrics: ['sessions', 'totalUsers', 'screenPageViews', 'bounceRate'],
              limit: 15,
              orderBys: [{ metric: { metricName: 'sessions' }, desc: true }]
            })
          : Promise.reject(new Error('No GA4'))
      );

      // GA4: Device category
      fetchPromises.push(
        ga4PropertyId && gscAccountId
          ? api.post(`/api/integrations/google/ga4/report/${gscAccountId}`, {
              propertyId: ga4PropertyId, startDate, endDate,
              dimensions: ['deviceCategory'],
              metrics: ['sessions', 'totalUsers'],
              limit: 10,
              orderBys: [{ metric: { metricName: 'sessions' }, desc: true }]
            })
          : Promise.reject(new Error('No GA4'))
      );

      // GA4: New vs returning
      fetchPromises.push(
        ga4PropertyId && gscAccountId
          ? api.post(`/api/integrations/google/ga4/report/${gscAccountId}`, {
              propertyId: ga4PropertyId, startDate, endDate,
              dimensions: ['newVsReturning'],
              metrics: ['sessions', 'totalUsers'],
              limit: 10
            })
          : Promise.reject(new Error('No GA4'))
      );

      // GA4: City
      fetchPromises.push(
        ga4PropertyId && gscAccountId
          ? api.post(`/api/integrations/google/ga4/report/${gscAccountId}`, {
              propertyId: ga4PropertyId, startDate, endDate,
              dimensions: ['city'],
              metrics: ['sessions', 'totalUsers'],
              limit: 15,
              orderBys: [{ metric: { metricName: 'totalUsers' }, desc: true }]
            })
          : Promise.reject(new Error('No GA4'))
      );

      // GA4: Landing page
      fetchPromises.push(
        ga4PropertyId && gscAccountId
          ? api.post(`/api/integrations/google/ga4/report/${gscAccountId}`, {
              propertyId: ga4PropertyId, startDate, endDate,
              dimensions: ['landingPagePlusQueryString'],
              metrics: ['sessions', 'totalUsers', 'bounceRate', 'averageSessionDuration'],
              limit: 15,
              orderBys: [{ metric: { metricName: 'sessions' }, desc: true }]
            })
          : Promise.reject(new Error('No GA4'))
      );

      // GA4: Events/conversions
      fetchPromises.push(
        ga4PropertyId && gscAccountId
          ? api.post(`/api/integrations/google/ga4/report/${gscAccountId}`, {
              propertyId: ga4PropertyId, startDate, endDate,
              dimensions: ['eventName'],
              metrics: ['eventCount', 'totalUsers'],
              limit: 20,
              orderBys: [{ metric: { metricName: 'eventCount' }, desc: true }]
            })
          : Promise.reject(new Error('No GA4'))
      );

      // GA4: Session source (for donut chart)
      fetchPromises.push(
        ga4PropertyId && gscAccountId
          ? api.post(`/api/integrations/google/ga4/report/${gscAccountId}`, {
              propertyId: ga4PropertyId, startDate, endDate,
              dimensions: ['sessionSource'],
              metrics: ['totalUsers'],
              limit: 10,
              orderBys: [{ metric: { metricName: 'totalUsers' }, desc: true }]
            })
          : Promise.reject(new Error('No GA4'))
      );

      // Rank tracking
      fetchPromises.push(api.get('/api/rank-tracking/').catch(e => ({ data: { keywords: [] } })));

      // Social media latest
      fetchPromises.push(
        api.get(`/api/social-media/latest?domain=${encodeURIComponent(currentDomain)}`).catch(e => ({ data: {} }))
      );

      // Site audit
      fetchPromises.push(
        api.get(`/api/data-for-seo/site-audit/saved/${encodeURIComponent(currentDomain)}`).catch(e => ({ data: {} }))
      );

      // Backlink analysis
      fetchPromises.push(
        api.post('/api/data-for-seo/backlink-analysis', { domain: currentDomain }).catch(e => ({ data: {} }))
      );

      const results = await Promise.allSettled(fetchPromises);

// --- Cron report data.json (Google Ads / AEO), Path B ---
let ppcData = null, aeoData = null;
try {
  const slugify = (x) => (x||'').toLowerCase().replace(/^https?:\/\//,'').replace(/^www\./,'').replace(/\/.*$/,'').replace(/\.[a-z]+$/,'').replace(/[^a-z0-9]/g,'');
  const domSlug = slugify(currentDomain);
  const djRes = await fetch('/reports/data.json?_=' + Date.now());
  if (djRes.ok) {
    const dj = await djRes.json();
    const clientKeys = Object.keys(dj.data || {});
    const matchKey = clientKeys.find(k => slugify(k) === domSlug || slugify((dj.clients && dj.clients[k]) || '') === domSlug);
    if (matchKey) {
      const weeks = Object.keys(dj.data[matchKey] || {}).sort();
      const latest = weeks[weeks.length - 1];
      const wk = latest ? dj.data[matchKey][latest] : null;
      if (wk) { ppcData = wk.ppc || null; aeoData = wk.aeo || null; }
    }
  }
} catch (e) { /* data.json optional */ }


      // Check for rate limits
      const rateLimitedResults = results.filter(isRateLimitError);
      if (rateLimitedResults.length > 0) {
        setRateLimited(true);
        if (rateLimitedResults.length >= results.length / 2) {
          setError('Rate limit reached. Please wait a few minutes and try again.');
          setLoading(false);
          return;
        }
      }

      const val = (r) => r.status === 'fulfilled' ? r.value.data : null;

      const gscRaw = val(results[0]);
      const channelData = val(results[1]);
      const pagePathData = val(results[2]);
      const sourceMediumData = val(results[3]);
      const deviceData = val(results[4]);
      const newReturningData = val(results[5]);
      const cityData = val(results[6]);
      const landingPageData = val(results[7]);
      const eventsData = val(results[8]);
      const sessionSourceData = val(results[9]);
      const rankRaw = val(results[10]);
      const socialRaw = val(results[11]);
      const auditRaw = val(results[12]);
      const backlinkRaw = val(results[13]);

      // Parse GSC
      const gscData = gscRaw?.gsc ? {
        totals: gscRaw.gsc.current || {},
        prev: gscRaw.gsc.previous || gscRaw.gsc.prev || {},
        changes: {
          clicks: (gscRaw.gsc.current?.clicks || 0) - (gscRaw.gsc.previous?.clicks || gscRaw.gsc.prev?.clicks || 0),
          impressions: (gscRaw.gsc.current?.impressions || 0) - (gscRaw.gsc.previous?.impressions || gscRaw.gsc.prev?.impressions || 0),
          ctr: (gscRaw.gsc.current?.ctr || 0) - (gscRaw.gsc.previous?.ctr || gscRaw.gsc.prev?.ctr || 0),
          position: (gscRaw.gsc.current?.position || 0) - (gscRaw.gsc.previous?.position || gscRaw.gsc.prev?.position || 0)
        },
        topQueries: gscRaw.gsc.topQueries || [],
        topPages: gscRaw.gsc.topPages || []
      } : null;

      // Parse GA4 overview
      const ga4Data = gscRaw?.ga4 || null;

      // Parse GA4 dimensional reports
      const parseGA4Rows = (data) => {
        if (!data?.rows) return [];
        return data.rows.map(r => ({
          dimension: r.dimensions?.[0] || '',
          metrics: (r.metrics || []).map(v => parseFloat(v) || 0)
        }));
      };

      // Parse rank tracking - filter by current domain
      const allKeywords = rankRaw?.keywords || rankRaw?.data || rankRaw || [];
      const domainKeywords = Array.isArray(allKeywords)
        ? allKeywords.filter(kw => {
            const norm = (d) => (d || '').toLowerCase().replace(/^https?:\/\//,'').replace(/^www\./,'').replace(/\/.*$/,'').trim();
            const kwD = norm(kw.domain); const curD = norm(currentDomain);
            return !kwD || !curD || kwD === curD || kwD.includes(curD) || curD.includes(kwD);
          })
          .map(kw => ({ ...kw, currentRank: kw.currentRank ?? kw.current_rank ?? kw.rank ?? kw.currentPosition ?? kw.position ?? null, previousRank: kw.previousRank ?? kw.previous_rank ?? kw.previousPosition ?? null }))
        : [];

      setReportData({
        gsc: gscData,
        ga4: ga4Data,
        channels: parseGA4Rows(channelData),
        pagePaths: parseGA4Rows(pagePathData),
        sourceMedium: parseGA4Rows(sourceMediumData),
        devices: parseGA4Rows(deviceData),
        newReturning: parseGA4Rows(newReturningData),
        cities: parseGA4Rows(cityData),
        landingPages: parseGA4Rows(landingPageData),
        events: parseGA4Rows(eventsData),
        sessionSources: parseGA4Rows(sessionSourceData),
        rankings: domainKeywords,
        social: socialRaw?.analysis || socialRaw || null,
        audit: auditRaw || null,
        backlinks: backlinkRaw || null,
        ppc: ppcData,
        aeo: aeoData,
        generatedAt: new Date().toISOString(),
        domain: currentDomain,
        channelHeaders: channelData?.headers || {},
        pagePathHeaders: pagePathData?.headers || {},
        sourceMediumHeaders: sourceMediumData?.headers || {},
        eventsHeaders: eventsData?.headers || {}
      });
    } catch (err) {
      console.error('Failed to fetch report data:', err);
      if (err?.response?.status === 429) {
        setRateLimited(true);
        setError('Rate limit reached. Please wait a few minutes and try again.');
      } else {
        setError('Failed to load report data. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [currentDomain, dateRange, getDateRanges]);

  // Re-fetch when domain changes
  useEffect(() => {
    if (currentDomain && currentDomain !== prevDomainRef.current) {
      fetchReportData();
    }
  }, [currentDomain, fetchReportData]);

  // Initial fetch
  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);


  // Domain change handler
  const handleDomainChange = (e) => {
    selectDomain(e.target.value);
  };

  const handleExportPDF = async () => {
    if (!reportRef.current || exporting) return;
    setExporting(true);
    try {
      const el = reportRef.current;
      const origBg = el.style.backgroundColor;

      // Inject a temporary style tag that forces light-mode colors for PDF export
      const pdfStyle = document.createElement('style');
      pdfStyle.id = 'pdf-export-override';
      pdfStyle.textContent = `
        #pdf-export-root,
        #pdf-export-root * {
          color-scheme: light !important;
        }
        #pdf-export-root {
          background-color: #ffffff !important;
          color: #1f2937 !important;
        }
        #pdf-export-root div,
        #pdf-export-root section,
        #pdf-export-root td,
        #pdf-export-root th,
        #pdf-export-root tr,
        #pdf-export-root table,
        #pdf-export-root p,
        #pdf-export-root span,
        #pdf-export-root h1,
        #pdf-export-root h2,
        #pdf-export-root h3,
        #pdf-export-root h4 {
          color: inherit !important;
          border-color: #e5e7eb !important;
        }
        #pdf-export-root [class*="bg-gray-5"],
        #pdf-export-root [class*="bg-gray-1"] {
          background-color: #f9fafb !important;
        }
        #pdf-export-root [class*="bg-white"] {
          background-color: #ffffff !important;
        }
        #pdf-export-root [class*="text-gray-9"],
        #pdf-export-root [class*="text-gray-8"] {
          color: #111827 !important;
        }
        #pdf-export-root [class*="text-gray-7"],
        #pdf-export-root [class*="text-gray-6"] {
          color: #374151 !important;
        }
        #pdf-export-root [class*="text-gray-5"],
        #pdf-export-root [class*="text-gray-4"],
        #pdf-export-root [class*="text-gray-3"] {
          color: #6b7280 !important;
        }
        #pdf-export-root [class*="border-gray"] {
          border-color: #e5e7eb !important;
        }
        #pdf-export-root [class*="bg-blue-5"] { background-color: #eff6ff !important; }
        #pdf-export-root [class*="bg-purple-5"] { background-color: #f5f3ff !important; }
        #pdf-export-root [class*="bg-green-5"] { background-color: #f0fdf4 !important; }
        #pdf-export-root [class*="bg-amber-5"] { background-color: #fffbeb !important; }
        #pdf-export-root [class*="bg-red-5"] { background-color: #fef2f2 !important; }
        #pdf-export-root [class*="bg-indigo-5"] { background-color: #eef2ff !important; }
        #pdf-export-root [class*="bg-cyan-5"] { background-color: #ecfeff !important; }
        #pdf-export-root [class*="bg-violet-5"] { background-color: #f5f3ff !important; }
        #pdf-export-root [class*="bg-yellow-5"] { background-color: #fefce8 !important; }
        #pdf-export-root svg { color: inherit !important; }
        #pdf-export-root [class*="text-blue"] { color: #2563eb !important; }
        #pdf-export-root [class*="text-green-5"] { color: #22c55e !important; }
        #pdf-export-root [class*="text-red-5"] { color: #ef4444 !important; }
      `;
      document.head.appendChild(pdfStyle);

      // Tag the element for CSS targeting
      const origId = el.id;
      el.id = 'pdf-export-root';
      el.style.backgroundColor = '#ffffff';

      // Small delay to let styles apply
      await new Promise(r => setTimeout(r, 100));

      const canvas = await html2canvas(el, { scale: 2, useCORS: true,
            backgroundColor: '#ffffff', logging: false, windowWidth: 1200 });

      // Restore original state
      el.style.backgroundColor = origBg;
      el.id = origId || '';
      pdfStyle.remove();

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfW = pdf.internal.pageSize.getWidth(), pdfH = pdf.internal
            .pageSize.getHeight();
      const margin = 10, availW = pdfW - margin * 2;
      const ratio = availW / canvas.width;
      const scaledH = canvas.height * ratio;
      if (scaledH > pdfH - margin * 2) {
        const pageH = pdfH - margin * 2 - 6;
        const sliceH = Math.floor(pageH / ratio);
        let srcY = 0, page = 0;
        while (srcY < canvas.height) {
          if (page > 0) { pdf.addPage(); pdf.setFillColor(255,255,255); pdf.rect
                (0,0,pdfW,pdfH,'F'); }
          const curH = Math.min(sliceH, canvas.height - srcY);
          const tc = document.createElement('canvas');
          tc.width = canvas.width; tc.height = curH;
          const ctx = tc.getContext('2d');
          ctx.fillStyle = '#fff'; ctx.fillRect(0,0,tc.width,tc.height);
          ctx.drawImage(canvas, 0, srcY, canvas.width, curH, 0, 0, canvas.width,
                curH);
          pdf.addImage(tc.toDataURL('image/png'), 'PNG', margin, margin, availW,
                curH * ratio);
          pdf.setFontSize(7); pdf.setTextColor(150,150,150);
          pdf.text('Generated by SERPfinity', pdfW / 2, pdfH - 4, { align:
                'center' });
          srcY += curH; page++;
        }
      } else {
        pdf.setFillColor(255,255,255); pdf.rect(0,0,pdfW,pdfH,'F');
        pdf.addImage(canvas.toDataURL('image/png'), 'PNG', margin, margin,
              availW, scaledH);
        pdf.setFontSize(7); pdf.setTextColor(150,150,150);
        pdf.text('Generated by SERPfinity', pdfW / 2, pdfH - 4, { align:
              'center' });
      }
      const ds = new Date().toLocaleDateString('en-US',{day:'numeric',month
            :'short',year:'numeric'}).replace(/\s/g,'_');
      const dc = currentDomain.replace(/[^a-zA-Z0-9]/g, '_');
      pdf.save(`Weekly_SEO_SEM_Report-${dc}-${ds}.pdf`);
    } catch (err) { console.error('PDF export error:', err); }
    setExporting(false);
  };

  // Formatters
  const fmt = (num) => {
    if (num == null) return '';
    if (Math.abs(num) >= 1e6) return (num / 1e6).toFixed(1) + 'M';
    if (Math.abs(num) >= 1e3) return (num / 1e3).toFixed(1) + 'K';
    return num.toLocaleString();
  };
  const fmtPct = (n) => n == null ? '' : (n * 100).toFixed(1) + '%';
  const fmtPctRaw = (n) => n == null ? '' : Number(n).toFixed(1) + '%';
  const fmtDur = (sec) => {
    if (!sec) return '0s';
    const m = Math.floor(sec / 60), s = Math.round(sec % 60);
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };
  const trendIcon = (ch) => {
    if (!ch && ch !== 0) return <Minus className="w-4 h-4 text-gray-400" />;
    if (ch > 0) return <ArrowUpRight className="w-4 h-4 text-green-500" />;
    if (ch < 0) return <ArrowDownRight className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-gray-400" />;
  };
  const trendColor = (ch, inv) => {
    if (!ch && ch !== 0) return 'text-gray-400';
    if (inv) return ch > 0 ? 'text-red-500' : ch < 0 ? 'text-green-500' : 'text-gray-400';
    return ch > 0 ? 'text-green-500' : ch < 0 ? 'text-red-500' : 'text-gray-400';
  };
  const scoreColor = (s) => s >= 80 ? 'text-green-600 bg-green-50 border-green-200' : s >= 60 ? 'text-yellow-600 bg-yellow-50 border-yellow-200' : 'text-red-600 bg-red-50 border-red-200';
  const scoreBarColor = (s) => s >= 80 ? 'bg-green-500' : s >= 60 ? 'bg-yellow-500' : 'bg-red-500';

  const reportDate = new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  const periodLabel = dateRange === '7d' ? 'Last 7 Days'
    : dateRange === '28d' ? 'Last 28 Days'
    : dateRange === '3m' ? 'Last 3 Months'
    : dateRange === 'custom' ? ((customStart && customEnd) ? `${customStart} to ${customEnd}` : 'Custom Range')
    : 'Last 3 Months';

  // Extract data safely
  const gsc = reportData?.gsc || {};
  const ga4 = reportData?.ga4 || {};
  const clicks = gsc?.totals?.clicks ?? null;
  const impressions = gsc?.totals?.impressions ?? null;
  const ctr = gsc?.totals?.ctr ?? null;
  const position = gsc?.totals?.position ?? null;
  const clicksCh = gsc?.changes?.clicks ?? null;
  const impressionsCh = gsc?.changes?.impressions ?? null;
  const ctrCh = gsc?.changes?.ctr ?? null;
  const positionCh = gsc?.changes?.position ?? null;
  const topQueries = gsc?.topQueries || [];
  const topPages = gsc?.topPages || [];
  const channels = reportData?.channels || [];
  const pagePaths = reportData?.pagePaths || [];
  const sourceMedium = reportData?.sourceMedium || [];
  const devices = reportData?.devices || [];
  const newReturning = reportData?.newReturning || [];
  const cities = reportData?.cities || [];
  const landingPages = reportData?.landingPages || [];
  const events = reportData?.events || [];
  const sessionSources = reportData?.sessionSources || [];
  const rankings = reportData?.rankings || [];
  const social = reportData?.social || null;
  const audit = reportData?.audit || null;
  const backlinks = reportData?.backlinks || null;
  const ppc = reportData?.ppc || null;
  const aeo = reportData?.aeo || null;

  // GA4 overview metrics
  const ga4Sessions = ga4?.current?.sessions ?? null;
  const ga4Users = ga4?.current?.totalUsers ?? null;
  const ga4Pageviews = ga4?.current?.screenPageViews ?? null;
  const ga4Bounce = ga4?.current?.bounceRate ?? null;
  const ga4Duration = ga4?.current?.averageSessionDuration ?? null;
  const ga4Conversions = ga4?.current?.conversions ?? null;


  return (
    <div className="min-h-screen bg-gray-50">
      {/* Toolbar */}
      <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <FileText className="w-5 h-5 text-blue-600" />
          <h1 className="text-lg font-semibold text-gray-800">Weekly SEO & SEM Performance Report</h1>
        </div>
        <div className="flex items-center gap-3">
          {domains && domains.length > 0 && (
            <div className="relative">
              <select value={currentDomain} onChange={handleDomainChange}
                className="appearance-none bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 pr-8 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-200">
                {domains.map(d => <option key={d.domain} value={d.domain}>{d.domain}</option>)}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          )}
          <div className="relative">
            <select value={dateRange} onChange={(e) => setDateRange(e.target.value)}
              className="appearance-none bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 pr-8 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-200">
              <option value="7d">Last 7 Days</option>
              <option value="28d">Last 28 Days</option>
              <option value="3m">Last 3 Months</option>
              <option value="custom">Custom Range</option>
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>
          {dateRange === 'custom' && (
            <div className="flex items-center gap-2">
              <input type="date" value={customStart} max={customEnd || undefined} onChange={(e) => setCustomStart(e.target.value)} className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-200" />
              <span className="text-gray-400 text-sm">to</span>
              <input type="date" value={customEnd} min={customStart || undefined} onChange={(e) => setCustomEnd(e.target.value)} className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-200" />
            </div>
          )}
          <button onClick={fetchReportData} disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
          <button onClick={handleExportPDF} disabled={exporting || loading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50">
            <Download className="w-4 h-4" /> {exporting ? 'Exporting...' : 'Export PDF'}
          </button>
        </div>
      </div>

      {/* Report Content */}
      <div className="max-w-5xl mx-auto py-8 px-4">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-32">
            <Loader2 className="w-10 h-10 text-blue-500 animate-spin mb-4" />
            <p className="text-gray-500 text-lg">Generating report for {currentDomain}...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-32">
            <AlertCircle className={`w-10 h-10 ${rateLimited ? 'text-yellow-400' : 'text-red-400'} mb-4`} />
            <p className="text-gray-600 text-lg mb-2">{error}</p>
            <button onClick={fetchReportData} className="text-blue-600 hover:underline text-sm">Try Again</button>
          </div>
        ) : (
          <div ref={reportRef} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            {/* Report Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-8 py-6 text-white">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-2xl font-bold mb-1">Weekly SEO & SEM Performance Report</h2>
                  <p className="text-blue-100 text-sm flex items-center gap-2">
                    <Globe className="w-4 h-4" /> {currentDomain}
                  </p>
                </div>
                <div className="text-right text-sm text-blue-100">
                  <p className="flex items-center gap-1 justify-end"><Calendar className="w-4 h-4" /> {reportDate}</p>
                  <p className="mt-1">Period: {periodLabel}</p>
                </div>
              </div>
            </div>

            {/* ═══ SYSTEM OVERVIEW ═══ */}
            <div className="px-8 py-6 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Activity className="w-5 h-5 text-blue-600" /> System Overview
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: 'Total Users', value: ga4Users, icon: Users, color: 'blue' },
                  { label: 'Sessions', value: ga4Sessions, icon: Activity, color: 'purple' },
                  { label: 'Pageviews', value: ga4Pageviews, icon: Eye, color: 'green' },
                  { label: 'Avg Duration', value: ga4Duration, icon: BarChart3, color: 'amber', format: 'duration' },
                  { label: 'Bounce Rate', value: ga4Bounce, icon: TrendingDown, color: 'red', format: 'pctRaw' },
                  { label: 'Conversions', value: ga4Conversions, icon: Target, color: 'indigo', format: 'number' },
                  { label: 'GSC Clicks', value: clicks, icon: MousePointerClick, color: 'cyan' },
                  { label: 'GSC Impressions', value: impressions, icon: Eye, color: 'violet' },
                ].map((kpi, i) => {
                  const Icon = kpi.icon;
                  const displayVal = kpi.format === 'duration' ? fmtDur(kpi.value) :
                    kpi.format === 'pctRaw' ? fmtPctRaw(kpi.value) :
                    fmt(kpi.value);
                  return (
                    <div key={i} className={`bg-${kpi.color}-50 rounded-xl p-4 border border-${kpi.color}-100`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">{kpi.label}</span>
                        <Icon className="w-4 h-4 text-gray-400" />
                      </div>
                      <p className="text-2xl font-bold text-gray-900">{displayVal || '—'}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ═══ TOP TRAFFIC CHANNELS ═══ */}
            {channels.length > 0 && (
              <div className="px-8 py-6 border-b border-gray-100">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <PieChart className="w-5 h-5 text-blue-600" /> Top Traffic Channels
                </h3>
                <div className="flex items-start gap-8">
                  <div className="flex-shrink-0">
                    <MiniDonut data={channels.slice(0, 8).map(c => ({ label: c.dimension, value: c.metrics[0] || 0 }))} size={180} />
                  </div>
                  <div className="flex-1">
                    <div className="grid grid-cols-2 gap-2">
                      {channels.slice(0, 8).map((c, i) => {
                        const totalSessions = channels.reduce((s, ch) => s + (ch.metrics[0] || 0), 0);
                        const pct = totalSessions > 0 ? ((c.metrics[0] / totalSessions) * 100).toFixed(1) : 0;
                        return (
                          <div key={i} className="flex items-center gap-2 text-sm">
                            <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                            <span className="text-gray-700 truncate">{c.dimension}</span>
                            <span className="text-gray-500 ml-auto">{pct}%</span>
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-xs text-gray-400 mt-3">Total: {fmt(channels.reduce((s, c) => s + (c.metrics[0] || 0), 0))} sessions</p>
                  </div>
                </div>
              </div>
            )}


            {/* ═══ METRICS: SESSION DEFAULT CHANNEL GROUP ═══ */}
            {channels.length > 0 && (
              <div className="px-8 py-6 border-b border-gray-100">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-blue-600" /> Metrics: Session Default Channel Group
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-2 text-gray-500 font-medium">Channel</th>
                        <th className="text-right py-3 px-2 text-gray-500 font-medium">Sessions</th>
                        <th className="text-right py-3 px-2 text-gray-500 font-medium">Users</th>
                        <th className="text-right py-3 px-2 text-gray-500 font-medium">Pageviews</th>
                        <th className="text-right py-3 px-2 text-gray-500 font-medium">Bounce Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {channels.slice(0, 10).map((c, i) => (
                        <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                          <td className="py-2.5 px-2 text-gray-800 font-medium">{c.dimension}</td>
                          <td className="py-2.5 px-2 text-right text-gray-700">{fmt(c.metrics[0])}</td>
                          <td className="py-2.5 px-2 text-right text-gray-700">{fmt(c.metrics[1])}</td>
                          <td className="py-2.5 px-2 text-right text-gray-700">{fmt(c.metrics[2])}</td>
                          <td className="py-2.5 px-2 text-right text-gray-700">{fmtPctRaw(c.metrics[3])}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ═══ METRICS: PAGE PATH (Top Pageviews) ═══ */}
            {pagePaths.length > 0 && (
              <div className="px-8 py-6 border-b border-gray-100">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Layout className="w-5 h-5 text-blue-600" /> Metrics: Page Path
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-2 text-gray-500 font-medium">Page Path</th>
                        <th className="text-right py-3 px-2 text-gray-500 font-medium">Views</th>
                        <th className="text-right py-3 px-2 text-gray-500 font-medium">Sessions</th>
                        <th className="text-right py-3 px-2 text-gray-500 font-medium">Avg Duration</th>
                        <th className="text-right py-3 px-2 text-gray-500 font-medium">Engagement Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagePaths.slice(0, 15).map((p, i) => (
                        <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                          <td className="py-2.5 px-2 text-gray-800 font-medium truncate max-w-xs" title={p.dimension}>
                            {p.dimension.length > 50 ? p.dimension.substring(0, 50) + '...' : p.dimension}
                          </td>
                          <td className="py-2.5 px-2 text-right text-gray-700">{fmt(p.metrics[0])}</td>
                          <td className="py-2.5 px-2 text-right text-gray-700">{fmt(p.metrics[1])}</td>
                          <td className="py-2.5 px-2 text-right text-gray-700">{fmtDur(p.metrics[2])}</td>
                          <td className="py-2.5 px-2 text-right text-gray-700">{fmtPctRaw(p.metrics[3])}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ═══ SEO SECTION ═══ */}
            {(topQueries.length > 0 || topPages.length > 0) && (
              <div className="px-8 py-6 border-b border-gray-100">
                <h3 className="text-xl font-bold text-gray-800 mb-1 flex items-center gap-2">
                  <Search className="w-5 h-5 text-green-600" /> SEO
                </h3>
                <p className="text-sm text-gray-500 mb-4">Search Console Performance</p>

                {/* GSC KPIs */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                    <span className="text-xs font-medium text-blue-600 uppercase">Clicks</span>
                    <p className="text-2xl font-bold text-gray-900">{fmt(clicks)}</p>
                    {clicksCh != null && <div className={`flex items-center gap-1 mt-1 text-xs font-medium ${trendColor(clicksCh)}`}>{trendIcon(clicksCh)} {clicksCh > 0 ? '+' : ''}{fmt(clicksCh)}</div>}
                  </div>
                  <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
                    <span className="text-xs font-medium text-purple-600 uppercase">Impressions</span>
                    <p className="text-2xl font-bold text-gray-900">{fmt(impressions)}</p>
                    {impressionsCh != null && <div className={`flex items-center gap-1 mt-1 text-xs font-medium ${trendColor(impressionsCh)}`}>{trendIcon(impressionsCh)} {impressionsCh > 0 ? '+' : ''}{fmt(impressionsCh)}</div>}
                  </div>
                  <div className="bg-green-50 rounded-xl p-4 border border-green-100">
                    <span className="text-xs font-medium text-green-600 uppercase">Avg CTR</span>
                    <p className="text-2xl font-bold text-gray-900">{ctr != null ? (ctr < 1 ? fmtPct(ctr) : ctr.toFixed(1) + '%') : '—'}</p>
                  </div>
                  <div className="bg-amber-50 rounded-xl p-4 border border-amber-100">
                    <span className="text-xs font-medium text-amber-600 uppercase">Avg Position</span>
                    <p className="text-2xl font-bold text-gray-900">{position != null ? position.toFixed(1) : '—'}</p>
                  </div>
                </div>

                {/* Top Queries (Keyword) Table */}
                {topQueries.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-md font-semibold text-gray-700 mb-3">Metrics: Query</h4>
                    <table className="w-full text-sm">
                      <thead><tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-2 text-gray-500 font-medium">Query</th>
                        <th className="text-right py-3 px-2 text-gray-500 font-medium">Clicks</th>
                        <th className="text-right py-3 px-2 text-gray-500 font-medium">Impressions</th>
                        <th className="text-right py-3 px-2 text-gray-500 font-medium">CTR</th>
                        <th className="text-right py-3 px-2 text-gray-500 font-medium">Position</th>
                      </tr></thead>
                      <tbody>
                        {topQueries.slice(0, 10).map((q, i) => (
                          <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                            <td className="py-2.5 px-2 text-gray-800 font-medium">{q.query || q.keys?.[0] || ''}</td>
                            <td className="py-2.5 px-2 text-right text-gray-700">{fmt(q.clicks)}</td>
                            <td className="py-2.5 px-2 text-right text-gray-700">{fmt(q.impressions)}</td>
                            <td className="py-2.5 px-2 text-right text-gray-700">{q.ctr != null ? (q.ctr < 1 ? fmtPct(q.ctr) : q.ctr.toFixed(1) + '%') : ''}</td>
                            <td className="py-2.5 px-2 text-right text-gray-700">{q.position != null ? q.position.toFixed(1) : ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Top Pages Table */}
                {topPages.length > 0 && (
                  <div>
                    <h4 className="text-md font-semibold text-gray-700 mb-3">Top Performing Pages</h4>
                    <table className="w-full text-sm">
                      <thead><tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-2 text-gray-500 font-medium">Page</th>
                        <th className="text-right py-3 px-2 text-gray-500 font-medium">Clicks</th>
                        <th className="text-right py-3 px-2 text-gray-500 font-medium">Impressions</th>
                        <th className="text-right py-3 px-2 text-gray-500 font-medium">CTR</th>
                        <th className="text-right py-3 px-2 text-gray-500 font-medium">Position</th>
                      </tr></thead>
                      <tbody>
                        {topPages.slice(0, 10).map((p, i) => {
                          const url = p.page || p.keys?.[0] || '';
                          return (
                            <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                              <td className="py-2.5 px-2 text-gray-800 font-medium truncate max-w-xs" title={url}>{url.length > 55 ? url.substring(0, 55) + '...' : url}</td>
                              <td className="py-2.5 px-2 text-right text-gray-700">{fmt(p.clicks)}</td>
                              <td className="py-2.5 px-2 text-right text-gray-700">{fmt(p.impressions)}</td>
                              <td className="py-2.5 px-2 text-right text-gray-700">{p.ctr != null ? (p.ctr < 1 ? fmtPct(p.ctr) : p.ctr.toFixed(1) + '%') : ''}</td>
                              <td className="py-2.5 px-2 text-right text-gray-700">{p.position != null ? p.position.toFixed(1) : ''}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}


            {/* ═══ METRICS: SESSION SOURCE / MEDIUM ═══ */}
            {sourceMedium.length > 0 && (
              <div className="px-8 py-6 border-b border-gray-100">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <ExternalLink className="w-5 h-5 text-blue-600" /> Metrics: Session Source / Medium
                </h3>
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-2 text-gray-500 font-medium">Source / Medium</th>
                    <th className="text-right py-3 px-2 text-gray-500 font-medium">Sessions</th>
                    <th className="text-right py-3 px-2 text-gray-500 font-medium">Users</th>
                    <th className="text-right py-3 px-2 text-gray-500 font-medium">Pageviews</th>
                    <th className="text-right py-3 px-2 text-gray-500 font-medium">Bounce Rate</th>
                  </tr></thead>
                  <tbody>
                    {sourceMedium.slice(0, 12).map((s, i) => (
                      <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                        <td className="py-2.5 px-2 text-gray-800 font-medium">{s.dimension}</td>
                        <td className="py-2.5 px-2 text-right text-gray-700">{fmt(s.metrics[0])}</td>
                        <td className="py-2.5 px-2 text-right text-gray-700">{fmt(s.metrics[1])}</td>
                        <td className="py-2.5 px-2 text-right text-gray-700">{fmt(s.metrics[2])}</td>
                        <td className="py-2.5 px-2 text-right text-gray-700">{fmtPctRaw(s.metrics[3])}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* ═══ METRICS: LANDING PAGE ═══ */}
            {landingPages.length > 0 && (
              <div className="px-8 py-6 border-b border-gray-100">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Layout className="w-5 h-5 text-blue-600" /> Metrics: Landing Page
                </h3>
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-2 text-gray-500 font-medium">Landing Page</th>
                    <th className="text-right py-3 px-2 text-gray-500 font-medium">Sessions</th>
                    <th className="text-right py-3 px-2 text-gray-500 font-medium">Users</th>
                    <th className="text-right py-3 px-2 text-gray-500 font-medium">Bounce Rate</th>
                    <th className="text-right py-3 px-2 text-gray-500 font-medium">Avg Duration</th>
                  </tr></thead>
                  <tbody>
                    {landingPages.slice(0, 10).map((lp, i) => (
                      <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                        <td className="py-2.5 px-2 text-gray-800 font-medium truncate max-w-xs" title={lp.dimension}>
                          {lp.dimension.length > 50 ? lp.dimension.substring(0, 50) + '...' : lp.dimension}
                        </td>
                        <td className="py-2.5 px-2 text-right text-gray-700">{fmt(lp.metrics[0])}</td>
                        <td className="py-2.5 px-2 text-right text-gray-700">{fmt(lp.metrics[1])}</td>
                        <td className="py-2.5 px-2 text-right text-gray-700">{fmtPctRaw(lp.metrics[2])}</td>
                        <td className="py-2.5 px-2 text-right text-gray-700">{fmtDur(lp.metrics[3])}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* ═══ AUDIENCE INSIGHTS ═══ */}
            {(devices.length > 0 || newReturning.length > 0 || cities.length > 0) && (
              <div className="px-8 py-6 border-b border-gray-100">
                <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-indigo-600" /> Audience Insights
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {/* Device Category */}
                  {devices.length > 0 && (
                    <div>
                      <h4 className="text-md font-semibold text-gray-700 mb-3 flex items-center gap-2">
                        <Monitor className="w-4 h-4" /> Device Category
                      </h4>
                      <div className="flex items-center gap-4">
                        <MiniDonut data={devices.map(d => ({ label: d.dimension, value: d.metrics[0] || 0 }))} size={130} />
                        <div className="space-y-2">
                          {devices.map((d, i) => {
                            const total = devices.reduce((s, x) => s + (x.metrics[0] || 0), 0);
                            const pct = total > 0 ? ((d.metrics[0] / total) * 100).toFixed(1) : 0;
                            const Icon = d.dimension === 'mobile' ? Smartphone : d.dimension === 'tablet' ? Tablet : Monitor;
                            return (
                              <div key={i} className="flex items-center gap-2 text-sm">
                                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                                <Icon className="w-3 h-3 text-gray-400" />
                                <span className="text-gray-700 capitalize">{d.dimension}</span>
                                <span className="text-gray-500 ml-auto font-medium">{pct}%</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* New vs Returning */}
                  {newReturning.length > 0 && (
                    <div>
                      <h4 className="text-md font-semibold text-gray-700 mb-3 flex items-center gap-2">
                        <Users className="w-4 h-4" /> New / Returning Users
                      </h4>
                      <div className="flex items-center gap-4">
                        <MiniDonut data={newReturning.map(d => ({ label: d.dimension, value: d.metrics[1] || 0 }))} size={130} />
                        <div className="space-y-2">
                          {newReturning.map((d, i) => {
                            const total = newReturning.reduce((s, x) => s + (x.metrics[1] || 0), 0);
                            const pct = total > 0 ? ((d.metrics[1] / total) * 100).toFixed(1) : 0;
                            return (
                              <div key={i} className="flex items-center gap-2 text-sm">
                                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                                <span className="text-gray-700 capitalize">{d.dimension || 'Unknown'}</span>
                                <span className="text-gray-500 ml-auto font-medium">{pct}%</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Users: City */}
                {cities.length > 0 && (
                  <div>
                    <h4 className="text-md font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      <MapPin className="w-4 h-4" /> Users: City
                    </h4>
                    <div className="flex items-start gap-6">
                      <MiniDonut data={cities.slice(0, 8).map(c => ({ label: c.dimension, value: c.metrics[1] || 0 }))} size={160} />
                      <table className="flex-1 text-sm">
                        <thead><tr className="border-b border-gray-200">
                          <th className="text-left py-2 px-2 text-gray-500 font-medium">City</th>
                          <th className="text-right py-2 px-2 text-gray-500 font-medium">Users</th>
                          <th className="text-right py-2 px-2 text-gray-500 font-medium">Sessions</th>
                        </tr></thead>
                        <tbody>
                          {cities.slice(0, 10).map((c, i) => (
                            <tr key={i} className="border-b border-gray-50">
                              <td className="py-2 px-2 text-gray-800">{c.dimension || '(not set)'}</td>
                              <td className="py-2 px-2 text-right text-gray-700">{fmt(c.metrics[1])}</td>
                              <td className="py-2 px-2 text-right text-gray-700">{fmt(c.metrics[0])}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ═══ USERS: SESSION SOURCE ═══ */}
            {sessionSources.length > 0 && (
              <div className="px-8 py-6 border-b border-gray-100">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Globe className="w-5 h-5 text-blue-600" /> Users: Session Source
                </h3>
                <div className="flex items-start gap-6">
                  <MiniDonut data={sessionSources.slice(0, 8).map(s => ({ label: s.dimension, value: s.metrics[0] || 0 }))} size={160} />
                  <div className="flex-1">
                    <div className="grid grid-cols-2 gap-2">
                      {sessionSources.slice(0, 8).map((s, i) => {
                        const total = sessionSources.reduce((sum, x) => sum + (x.metrics[0] || 0), 0);
                        const pct = total > 0 ? ((s.metrics[0] / total) * 100).toFixed(1) : 0;
                        return (
                          <div key={i} className="flex items-center gap-2 text-sm">
                            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                            <span className="text-gray-700 truncate">{s.dimension || '(direct)'}</span>
                            <span className="text-gray-500 ml-auto">{pct}%</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ═══ CONVERSIONS AND EVENTS ═══ */}
            {events.length > 0 && (
              <div className="px-8 py-6 border-b border-gray-100">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Target className="w-5 h-5 text-blue-600" /> Conversions and Events
                </h3>
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-2 text-gray-500 font-medium">Event Name</th>
                    <th className="text-right py-3 px-2 text-gray-500 font-medium">Event Count</th>
                    <th className="text-right py-3 px-2 text-gray-500 font-medium">Users</th>
                  </tr></thead>
                  <tbody>
                    {events.slice(0, 15).map((ev, i) => (
                      <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                        <td className="py-2.5 px-2 text-gray-800 font-medium">{ev.dimension}</td>
                        <td className="py-2.5 px-2 text-right text-gray-700">{fmt(ev.metrics[0])}</td>
                        <td className="py-2.5 px-2 text-right text-gray-700">{fmt(ev.metrics[1])}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}


            {/* ═══ POSITION TRACKING ═══ */}
            {rankings.length > 0 && (
              <div className="px-8 py-6 border-b border-gray-100">
                <h3 className="text-xl font-bold text-gray-800 mb-1 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-purple-600" /> Position Tracking
                </h3>
                <p className="text-sm text-gray-500 mb-4">Keyword rank tracking for {currentDomain}</p>

                {/* Position distribution */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                  {[
                    { label: 'Top 3', filter: kw => (kw.currentRank || kw.current_rank || kw.rank || 999) <= 3 },
                    { label: 'Top 10', filter: kw => (kw.currentRank || kw.current_rank || kw.rank || 999) <= 10 },
                    { label: 'Top 20', filter: kw => (kw.currentRank || kw.current_rank || kw.rank || 999) <= 20 },
                    { label: 'Top 100', filter: kw => (kw.currentRank || kw.current_rank || kw.rank || 999) <= 100 },
                    { label: 'Total', filter: () => true }
                  ].map((bucket, i) => (
                    <div key={i} className="bg-purple-50 rounded-lg p-3 text-center border border-purple-100">
                      <p className="text-xs text-purple-600 font-medium uppercase">{bucket.label}</p>
                      <p className="text-xl font-bold text-gray-900">{rankings.filter(bucket.filter).length}</p>
                    </div>
                  ))}
                </div>

                {/* Improved / Declined */}
                <div className="flex gap-4 mb-4">
                  <div className="flex items-center gap-2 text-sm">
                    <ArrowUpRight className="w-4 h-4 text-green-500" />
                    <span className="text-gray-600">Improved:</span>
                    <span className="font-bold text-green-600">
                      {rankings.filter(kw => {
                        const prev = kw.previousRank || kw.previous_rank || 0;
                        const curr = kw.currentRank || kw.current_rank || kw.rank || 0;
                        return prev > 0 && curr > 0 && curr < prev;
                      }).length}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <ArrowDownRight className="w-4 h-4 text-red-500" />
                    <span className="text-gray-600">Declined:</span>
                    <span className="font-bold text-red-600">
                      {rankings.filter(kw => {
                        const prev = kw.previousRank || kw.previous_rank || 0;
                        const curr = kw.currentRank || kw.current_rank || kw.rank || 0;
                        return prev > 0 && curr > 0 && curr > prev;
                      }).length}
                    </span>
                  </div>
                </div>

                {/* Keyword Table */}
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-2 text-gray-500 font-medium">Keyword</th>
                    <th className="text-right py-3 px-2 text-gray-500 font-medium">Current Rank</th>
                    <th className="text-right py-3 px-2 text-gray-500 font-medium">Previous</th>
                    <th className="text-right py-3 px-2 text-gray-500 font-medium">Change</th>
                    <th className="text-right py-3 px-2 text-gray-500 font-medium">Volume</th>
                  </tr></thead>
                  <tbody>
                    {rankings.slice(0, 15).map((kw, i) => {
                      const curr = kw.currentRank || kw.current_rank || kw.rank || 0;
                      const prev = kw.previousRank || kw.previous_rank || 0;
                      const change = prev > 0 && curr > 0 ? prev - curr : 0;
                      return (
                        <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                          <td className="py-2.5 px-2 text-gray-800 font-medium">{kw.keyword || kw.query || ''}</td>
                          <td className="py-2.5 px-2 text-right text-gray-700 font-semibold">{curr || '—'}</td>
                          <td className="py-2.5 px-2 text-right text-gray-500">{prev || '—'}</td>
                          <td className={`py-2.5 px-2 text-right font-medium ${change > 0 ? 'text-green-600' : change < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                            {change > 0 ? `+${change}` : change < 0 ? change : '—'}
                          </td>
                          <td className="py-2.5 px-2 text-right text-gray-500">{kw.searchVolume || kw.search_volume || kw.volume || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* ═══ BACKLINK PROFILE ═══ */}
            {backlinks && (backlinks.totalBacklinks > 0 || backlinks.overview) && (
              <div className="px-8 py-6 border-b border-gray-100">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Link2 className="w-5 h-5 text-blue-600" /> Backlink Profile
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  {[
                    { label: 'Total Backlinks', value: backlinks.totalBacklinks || backlinks.overview?.totalBacklinks || 0 },
                    { label: 'Referring Domains', value: backlinks.referringDomains || backlinks.overview?.referringDomains || 0 },
                    { label: 'Domain Authority', value: backlinks.domainAuthority || backlinks.overview?.domainAuthority || 0 },
                    { label: 'Dofollow', value: backlinks.dofollow || backlinks.overview?.dofollow || 0 },
                  ].map((m, i) => (
                    <div key={i} className="bg-gray-50 rounded-lg p-4 border border-gray-200 text-center">
                      <p className="text-xs text-gray-500 font-medium uppercase mb-1">{m.label}</p>
                      <p className="text-2xl font-bold text-gray-900">{fmt(m.value)}</p>
                    </div>
                  ))}
                </div>

                {/* Top referrers */}
                {backlinks.topReferrers && backlinks.topReferrers.length > 0 && (
                  <div>
                    <h4 className="text-md font-semibold text-gray-700 mb-2">Top Referring Domains</h4>
                    <table className="w-full text-sm">
                      <thead><tr className="border-b border-gray-200">
                        <th className="text-left py-2 px-2 text-gray-500 font-medium">Domain</th>
                        <th className="text-right py-2 px-2 text-gray-500 font-medium">Backlinks</th>
                        <th className="text-right py-2 px-2 text-gray-500 font-medium">DA</th>
                      </tr></thead>
                      <tbody>
                        {backlinks.topReferrers.slice(0, 10).map((ref, i) => (
                          <tr key={i} className="border-b border-gray-50">
                            <td className="py-2 px-2 text-gray-800">{ref.domain}</td>
                            <td className="py-2 px-2 text-right text-gray-700">{fmt(ref.backlinks)}</td>
                            <td className="py-2 px-2 text-right text-gray-700">{ref.da || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* ═══ SOCIAL MEDIA ═══ */}
            {social && social.platforms && (
              <div className="px-8 py-6 border-b border-gray-100">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Share2 className="w-5 h-5 text-pink-600" /> Social Media Presence
                </h3>
                {social.overallScore != null && (
                  <div className="mb-4">
                    <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium ${scoreColor(social.overallScore)}`}>
                      Overall Score: {social.overallScore}/100
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {(Array.isArray(social.platforms) ? social.platforms : Object.entries(social.platforms || {}).map(([k, v]) => ({
                    platform: k, ...v
                  }))).map((p, i) => (
                    <div key={i} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm font-semibold text-gray-800 capitalize">{p.platform || p.name || 'Platform'}</span>
                        {p.score != null && <span className={`text-xs px-2 py-0.5 rounded-full ${scoreColor(p.score)}`}>{p.score}</span>}
                      </div>
                      {p.followers != null && <p className="text-xs text-gray-500">Followers: {fmt(p.followers)}</p>}
                      {p.engagement != null && <p className="text-xs text-gray-500">Engagement: {fmtPctRaw(p.engagement)}</p>}
                      {p.posts != null && <p className="text-xs text-gray-500">Posts: {p.posts}</p>}
                      {p.url && <a href={p.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 hover:underline mt-1 block">View Profile</a>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ═══ SITE HEALTH ═══ */}
            {audit && (audit.score != null || audit.overallScore != null) && (
              <div className="px-8 py-6 border-b border-gray-100">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Target className="w-5 h-5 text-blue-600" /> Site Health Overview
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className={`rounded-xl p-5 border text-center ${scoreColor(audit.score ?? audit.overallScore)}`}>
                    <p className="text-xs font-medium uppercase tracking-wide mb-2">Overall Score</p>
                    <p className="text-4xl font-bold">{audit.score ?? audit.overallScore}</p>
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-3">
                      <div className={`h-2 rounded-full ${scoreBarColor(audit.score ?? audit.overallScore)}`}
                        style={{ width: `${audit.score ?? audit.overallScore}%` }} />
                    </div>
                  </div>
                  {audit.categories && Object.entries(audit.categories).slice(0, 2).map(([key, val]) => {
                    const cs = typeof val === 'object' ? (val.score || 0) : val;
                    return (
                      <div key={key} className={`rounded-xl p-5 border text-center ${scoreColor(cs)}`}>
                        <p className="text-xs font-medium uppercase tracking-wide mb-2">{key.replace(/_/g, ' ')}</p>
                        <p className="text-4xl font-bold">{cs}</p>
                        <div className="w-full bg-gray-200 rounded-full h-2 mt-3">
                          <div className={`h-2 rounded-full ${scoreBarColor(cs)}`} style={{ width: `${cs}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* PPC / GOOGLE ADS */}
{ppc && ppc.ads_status === 'live' && (
  <div className="px-8 py-6 border-b border-gray-100">
    <h3 className="text-xl font-bold text-gray-800 mb-1 flex items-center gap-2">
      <DollarSign className="w-5 h-5 text-green-600" /> Google Ads (PPC)
    </h3>
    <p className="text-sm text-gray-500 mb-4">Paid search performance</p>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      {(ppc.kpis||[]).map((k,i) => (
        <div key={i} className="bg-green-50 rounded-xl p-4 border border-green-100">
          <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">{k.label}</span>
          <p className="text-2xl font-bold text-gray-900">{k.icon==='$'?'$':''}{k.val}{k.icon==='%'?'%':''}</p>
          {k.delta != null && <div className={`flex items-center gap-1 mt-1 text-xs font-medium ${trendColor(k.delta, k.invert)}`}>{trendIcon(k.delta)} {k.delta > 0 ? '+' : ''}{k.delta}%</div>}
        </div>
      ))}
    </div>
    {(ppc.campaigns||[]).length > 0 && (<div className="mb-6">
      <h4 className="text-md font-semibold text-gray-700 mb-3">Campaigns</h4>
      <table className="w-full text-sm"><thead><tr className="border-b border-gray-200">
        <th className="text-left py-3 px-2 text-gray-500 font-medium">Campaign</th>
        <th className="text-right py-3 px-2 text-gray-500 font-medium">Cost</th>
        <th className="text-right py-3 px-2 text-gray-500 font-medium">Clicks</th>
        <th className="text-right py-3 px-2 text-gray-500 font-medium">Avg CPC</th>
        <th className="text-right py-3 px-2 text-gray-500 font-medium">Conv</th>
      </tr></thead><tbody>
        {ppc.campaigns.map((c,i) => (<tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
          <td className="py-2.5 px-2 text-gray-800 font-medium">{c.c}</td>
          <td className="py-2.5 px-2 text-right text-gray-700">${c.cost != null ? c.cost.toFixed(2) : '-'}</td>
          <td className="py-2.5 px-2 text-right text-gray-700">{fmt(c.clicks)}</td>
          <td className="py-2.5 px-2 text-right text-gray-700">${c.cpc != null ? c.cpc.toFixed(2) : '-'}</td>
          <td className="py-2.5 px-2 text-right text-gray-700">{c.conv}</td>
        </tr>))}
      </tbody></table>
    </div>)}
    {ppc.ads_gender && (<div><h4 className="text-md font-semibold text-gray-700 mb-2">Clicks by Gender</h4>
      <div className="flex gap-4">{Object.entries(ppc.ads_gender).map(([g,v],i) => (<div key={i} className="bg-gray-50 rounded-lg px-4 py-2 border border-gray-200 text-sm"><span className="text-gray-500 capitalize">{g.toLowerCase()}</span>: <span className="font-semibold text-gray-800">{fmt(v)}</span></div>))}</div>
    </div>)}
  </div>
)}

{/* AEO / AI VISIBILITY */}
{aeo && (aeo.kpis||[]).length > 0 && (
  <div className="px-8 py-6 border-b border-gray-100">
    <h3 className="text-xl font-bold text-gray-800 mb-1 flex items-center gap-2">
      <Target className="w-5 h-5 text-violet-600" /> AEO / AI Visibility
    </h3>
    <p className="text-sm text-gray-500 mb-4">Answer Engine Optimization &amp; AI search presence</p>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      {aeo.kpis.map((k,i) => (<div key={i} className="bg-violet-50 rounded-xl p-4 border border-violet-100">
        <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">{k.label}</span>
        <p className="text-2xl font-bold text-gray-900">{k.val}</p>
        {k.note && <p className="text-xs text-gray-400 mt-1">{k.note}</p>}
      </div>))}
    </div>
    {(aeo.topics||[]).length > 0 && (<div><h4 className="text-md font-semibold text-gray-700 mb-3">Tracked AI Topics</h4>
      <table className="w-full text-sm"><thead><tr className="border-b border-gray-200">
        <th className="text-left py-3 px-2 text-gray-500 font-medium">Keyword</th>
        <th className="text-right py-3 px-2 text-gray-500 font-medium">Position</th>
        <th className="text-right py-3 px-2 text-gray-500 font-medium">AI Cited</th>
      </tr></thead><tbody>
        {aeo.topics.map((t,i) => (<tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
          <td className="py-2.5 px-2 text-gray-800 font-medium">{t.keyword}</td>
          <td className="py-2.5 px-2 text-right text-gray-700">{t.position != null ? t.position : '-'}</td>
          <td className="py-2.5 px-2 text-right">{t.cited ? <span className="text-green-600 font-semibold">Yes</span> : <span className="text-gray-400">No</span>}</td>
        </tr>))}
      </tbody></table>
    </div>)}
  </div>
)}

              {/* Report Footer */}
            <div className="px-8 py-4 bg-gray-50 text-center">
              <p className="text-xs text-gray-400">
                Generated by SERPfinity on {reportDate} for {currentDomain}
              </p>
              <p className="text-xs text-gray-300 mt-1">
                Data sourced from Google Analytics, Search Console, rank tracking, backlinks, and social media.
              </p>
            </div>

            {/* No data fallback */}
            {!topQueries.length && !topPages.length && !channels.length && !pagePaths.length && !rankings.length &&
              !events.length && !(audit?.score || audit?.overallScore) && !social?.platforms && (
              <div className="px-8 py-12 text-center">
                <AlertCircle className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-lg font-medium mb-2">No Data Available Yet</p>
                <p className="text-gray-400 text-sm max-w-md mx-auto">
                  Connect Google Search Console/GA4, set up keyword tracking, or run a social media analysis
                  for {currentDomain} to populate this report.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default WeeklyReport;